import React from "react";
import { Box, Tab, Tabs } from "@origin-digital/ods-core";
import {
  IconElectricity,
  IconGas,
  IconHotWater,
  IconInternet,
  IconLPG,
} from "@origin-digital/ods-icons";
import { useUserContext } from "@origin-digital/user-context-provider";
import { IAccountService, ServiceType } from "@origin-digital/event-dispatcher";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@origin-digital/ods-types";

const SERVICE_DISPLAY_PROPS: Readonly<
  Record<
    ServiceType,
    { hidden?: boolean; icon?: React.ReactNode; label?: string }
  >
> = {
  [ServiceType.ELECTRICITY]: {
    icon: <IconElectricity />,
    label: "Electricity",
  },
  [ServiceType.NATURAL_GAS]: { icon: <IconGas />, label: "Gas" },
  [ServiceType.HOT_WATER]: { icon: <IconHotWater />, label: "Hot water" },
  [ServiceType.LPG]: { icon: <IconLPG />, label: "LPG" },
  [ServiceType.BROADBAND]: { icon: <IconInternet />, label: "Internet" },
  [ServiceType.OHM]: { hidden: true },
  [ServiceType.VPP_BATTERY]: { hidden: true },
  [ServiceType.UNKNOWN]: { hidden: true },
};

const ID_DELIMITER: string = "@@";

const createAccountServiceId = (accountService?: IAccountService) => {
  return accountService
    ? `${accountService.accountId}${ID_DELIMITER}${accountService.serviceId}`
    : "";
};

const extractAccountServiceId = (
  id: string
): { accountId: string; serviceId: string } => {
  const ids = id.split(ID_DELIMITER);
  return { accountId: ids[0], serviceId: ids[1] };
};

export const ServiceSelector = () => {
  const { property, setService } = useUserContext();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg")
  );

  const serviceDisplayList =
    property?.accountServices.filter(
      (accountService) =>
        !SERVICE_DISPLAY_PROPS[accountService.serviceType].hidden
    ) ?? [];

  if (!property || serviceDisplayList.length <= 1) {
    return null;
  }
  return (
    <Box
      data-id="service-selector"
      display="flex"
      justifyContent={isMobile ? "center" : undefined}
    >
      <Tabs
        size="small"
        hideDivider={true}
        variant="scrollable"
        scrollButtons={true}
        value={createAccountServiceId(property.selectedAccountService)}
        onChange={(_, accountServiceId) => {
          const { accountId, serviceId } = extractAccountServiceId(
            accountServiceId as string
          );
          const matchingService = property.accountServices.find(
            (accountService) =>
              accountService.accountId === accountId &&
              accountService.serviceId === serviceId
          );
          setService(matchingService!);
        }}
      >
        {serviceDisplayList.map((accountService) => (
          <Tab
            key={accountService.serviceId}
            value={createAccountServiceId(accountService)}
            label={SERVICE_DISPLAY_PROPS[accountService.serviceType].label}
            icon={SERVICE_DISPLAY_PROPS[accountService.serviceType].icon}
          />
        ))}
      </Tabs>
    </Box>
  );
};
